import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigationStatus } from '../hooks/useNavigationStatus';
import './DeviceList.css';
import { API_BASE_IOTHUB, authHeaders } from '../constants/api';
import { fetchListing } from '../utils/thingsboardEdition';
import Pagination from './Pagination';
import SearchWithSuggestions from './SearchWithSuggestions';
import { useAuth } from '../context/AuthContext';
import { Hinzufügen, Export } from '../icons';
import { useStatusMessage } from '../hooks/useStatusMessage';
import { StatusBar, DeleteConfirmModal, SortDropdown, IconButton, LoadingErrorState, PageToolbar } from '../UI-Elements';
import { generateSuggestions } from '../utils/suggestions';
import { formatDateDE } from '../utils/formatDate';

const PAGE_SIZE = 10;

function DeviceList() {
  const [deviceCount, setDeviceCount] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);

  const [sortOption, setSortOption] = useState("name");

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { authToken: token, iotHubToken } = useAuth();
  const navigate = useNavigate();

  const { statusMessage, statusType, showStatus } = useStatusMessage();

  useNavigationStatus(showStatus);

  // Delete device state
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [showDeviceDeleteConfirm, setShowDeviceDeleteConfirm] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState(false);

  useEffect(() => {
    async function loadDevices() {
      try {
        setLoading(true);
        setError("");
        const sortMap = {
          name: { property: "name", order: "ASC" },
          created: { property: "createdTime", order: "DESC" },
          activity: { property: "active", order: "DESC" }
        };
        const sort = sortMap[sortOption];
        const params = {
          pageSize: PAGE_SIZE,
          page,
          sortProperty: sort.property,
          sortOrder: sort.order,
          includeCustomers: true
        };
        if (searchTerm) {
          params.textSearch = searchTerm;
        }
        const res = await fetchListing('deviceInfos/all', params, token);
        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || errData?.message || `Fehler beim Laden der Geräte (HTTP ${res.status})`);
        }
        const data = await res.json();
        const normalizedDevices = (data.data || []).map(d => ({
          id: d.id.id,
          name: d.name,
          createdTime: d.createdTime,
          active: d.active,
          groups: (d.groups || [])
            .filter(g => g.name !== "All")
            .map(g => ({ id: g.id?.id ?? g.id, name: g.name }))
        }));
        setDevices(normalizedDevices);
        setDeviceCount(data.totalElements);
        setTotalPages(data.totalPages);
      } catch (err) {
        setError(err.message || "Fehler beim Laden der Geräte. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        setDevices([]);
        setDeviceCount(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    }
    if (token) {
      loadDevices();
    }
  }, [token, page, sortOption, searchTerm]);

  const suggestions = generateSuggestions(devices, searchTerm);

  async function confirmDeleteDevice() {
    if (!deviceToDelete) return;
    try {
      setDeletingDevice(true);
      const deleteRes = await fetch(
        `${API_BASE_IOTHUB}/api/v1/devices/${deviceToDelete.id}`,
        { method: 'DELETE', headers: authHeaders(iotHubToken) }
      );
      if (!deleteRes.ok) throw new Error(`Löschen fehlgeschlagen (HTTP ${deleteRes.status})`);

      setDevices(prev => prev.filter(d => d.id !== deviceToDelete.id));
      setDeviceCount(c => Math.max(0, (c ?? 1) - 1));
      showStatus(`Gerät „${deviceToDelete.name}“ wurde erfolgreich gelöscht.`);
    } catch (e) {
      console.error(e);
      showStatus(`Gerät konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`, 'error');
    } finally {
      setDeletingDevice(false);
      setShowDeviceDeleteConfirm(false);
      setDeviceToDelete(null);
    }
  }

  return (
    <div>
      {/* Toolbar */}
      <PageToolbar
        left={
          <>
            <h1 className="device-title">Alle Endgeräte ({deviceCount ?? "..."})</h1>
            <SortDropdown
              options={[
                { key: 'name', label: 'Name' },
                { key: 'created', label: 'Hinzugefügt' },
                { key: 'activity', label: 'Aktivität' },
              ]}
              value={sortOption}
              onChange={(key) => { setSortOption(key); setPage(0); }}
            />
          </>
        }
        right={
          <>
            <button
              type="button"
              className="export-button"
              aria-label="Endgerät hinzufügen"
              title="Endgerät hinzufügen"
              onClick={() => navigate('/add-device')}
            >
              <div className="export-icon"><Hinzufügen /></div>
              <div>Endgerät hinzufügen</div>
            </button>

            <button 
              type="button"
              className="export-button" 
              aria-label="Daten exportieren" 
              title="Daten exportieren" 
              onClick={() => navigate('/export-data')}
            >
              <div className="export-icon"><Export /></div>
              <div>Daten exportieren</div>
            </button>

            <SearchWithSuggestions
              ref={searchInputRef}
              id="device-search"
              label="Endgerät suchen"
              value={searchTerm}
              onChange={(val) => { setSearchTerm(val); setPage(0); }}
              suggestions={suggestions}
              suggestionsId="device-suggestions"
            />
          </>
        }
      />

      <StatusBar message={statusMessage} statusType={statusType} />

      {/* Liste */}
      <div className="device-table-container" role="region" aria-label="Geräteliste">
        <LoadingErrorState loading={loading} loadingText="Geräte werden geladen…" error={error}>
          <>
            <div className="device-list" role="list" aria-label="Geräte">
              <div className="device-row device-header" role="row">
                <div>Gerätename</div><div>Gruppen</div><div>Hinzugefügt</div>
                <div>Status</div><div>Öffnen</div><div>Löschen</div>
              </div>
              {(!devices || devices.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Geräte gefunden</div>
              ) : devices.map(d => (
                <div className="device-row device-item clickable-row" key={d.id} role="listitem"
                  onClick={(e) => { if (e.target.closest('button, .status-badge, .group-badge, .sort-wrapper')) return; navigate(`/devices/${d.id}`, { state: { name: d.name } }); }}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .status-badge, .group-badge, .sort-wrapper')) { e.preventDefault(); navigate(`/devices/${d.id}`, { state: { name: d.name } }); } }}
                  tabIndex={0}
                  aria-label={`Gerät ${d.name} öffnen`}
                >
                  <div>{d.name}</div>
                  <div className="group-badges" aria-label="Gruppen">
                    {d.groups.length ? d.groups.map(g => (
                      <span key={g.id ?? g.name} className="group-badge" title={`Gruppe: ${g.name}`}
                        role="button" tabIndex={0} style={{ cursor: 'pointer' }}
                        onClick={e => { e.stopPropagation(); navigate(`/device-groups/${g.id}`, { state: { name: g.name } }); }}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate(`/device-groups/${g.id}`, { state: { name: g.name } }); } }}
                      >{g.name}</span>
                    )) : "–"}
                  </div>
                  <div>{formatDateDE(d.createdTime)}</div>
                  <div className="cell-right">
                    <span className={`status-badge ${d.active ? "active" : "inactive"}`} aria-label={d.active ? "Aktiv" : "Inaktiv"}>
                      {d.active ? "Aktiv" : "Inaktiv"}
                    </span>
                  </div>
                  <div className="cell-right">
                    <IconButton variant="open" label={`Gerät ${d.name} öffnen`}
                      onClick={() => navigate(`/devices/${d.id}`, { state: { name: d.name } })} />
                  </div>
                  <div className="cell-right">
                    <IconButton variant="delete" label={`Gerät ${d.name} löschen`}
                      onClick={e => { e.stopPropagation(); setDeviceToDelete({ id: d.id, name: d.name }); setShowDeviceDeleteConfirm(true); }} />
                  </div>
                </div>
              ))}
            </div>
            {deviceCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={deviceCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Geräte" />
            )}
          </>
        </LoadingErrorState>
      </div>

      {/* Device Delete Confirmation Modal */}
      {showDeviceDeleteConfirm && deviceToDelete && (
        <DeleteConfirmModal
          titleId="del-device-title"
          title={<>Sind Sie sicher, dass Sie das Gerät <br /><i>{deviceToDelete.name}</i> löschen möchten?</>}
          body={<p>Nach der Bestätigung wird das Gerät und alle zugehörigen Daten <strong>unwiderruflich</strong> gelöscht.</p>}
          onConfirm={confirmDeleteDevice}
          onCancel={() => { setShowDeviceDeleteConfirm(false); setDeviceToDelete(null); }}
          loading={deletingDevice}
        />
      )}
    </div>
  );
}

export default DeviceList;